import { changeFollowableTypes } from "@nexxt/contracts/notifications/enums";
import { toast } from "@nexxt/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { LinkOptions } from "@tanstack/react-router";
import { BellOff, BellPlus, BellRing } from "lucide-react";
import { errorText } from "../errors";
import { m } from "../paraglide/messages.js";
import type { EntityAction } from "../shell/entity-actions";
import { type FollowedObject, type FollowLevel, type FollowTarget, sameObject } from "./followed-object";

// Following an object (ADR-281, ADR-307): the page of a PO, an inbound, a linesheet, an order or a claim offers to
// follow its status or all its changes, and they reach the bell. The list of what the user follows is read once and
// shared by every page and by the bell; the gateway and the pages of the objects are the app's (`FollowClient`).

/** The follows as the app reaches them: its gateway, and the page of each object. */
export interface FollowClient {
  list(): Promise<{ items: FollowedObject[] }>;
  /** Follows, or changes the level of what one follows. */
  follow(target: FollowTarget, level: FollowLevel): Promise<FollowedObject>;
  unfollow(target: FollowTarget): Promise<FollowTarget>;
  /** The page of the app the object is; null when it has none. */
  linkOf(object: FollowedObject): LinkOptions | null;
  /** Its status in the words of the app, as its badges say it: in the list of the follows and in the bell (ADR-308). */
  statusOf(object: Pick<FollowedObject, "entityType" | "status">): string;
}

const FOLLOWS_KEY = ["follows"] as const;

export const useFollowing = (client: FollowClient) => useQuery({ queryKey: FOLLOWS_KEY, queryFn: () => client.list() });

/** Whether the domain of the object tells its changes besides its status (ADR-307): then it is followed at `all` too. */
export const followsChanges = ({ entityType }: FollowTarget): boolean =>
  (changeFollowableTypes as readonly string[]).includes(entityType);

/** What a follow asks: a level to follow at, or null to stop following. */
type FollowChange = { target: FollowTarget; level: FollowLevel | null };

/** Follows at a level or stops following, then reads the list again; a refusal is said in a toast, a 422 too. */
export function useFollowToggle(client: FollowClient) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ target, level }: FollowChange) =>
      level === null ? client.unfollow(target) : client.follow(target, level),
    onSuccess: (_, { level }) =>
      void toast.success(
        level === null ? m.follow_stopped() : level === "all" ? m.follow_started_all() : m.follow_started(),
      ),
    onError: (error) => void toast.error(errorText(error)),
    onSettled: () => queryClient.invalidateQueries({ queryKey: FOLLOWS_KEY }),
  });
}

/**
 * The actions of the page of `target`, in its buttons and in ⌘K: "Follow status" and "Follow all changes" while it is
 * not followed, once it is the other level and "Unfollow"; for an order or a claim, whose changes no domain tells,
 * "Follow" and "Unfollow". None until the list is read, so the page never offers to follow what the user already
 * follows.
 */
export function useFollowAction(client: FollowClient, target: FollowTarget): EntityAction[] {
  const following = useFollowing(client);
  const toggle = useFollowToggle(client);
  if (!following.data) return [];
  const level = following.data.items.find((item) => sameObject(item, target))?.level ?? null;
  const follow = (to: FollowLevel | null) => () => toggle.mutate({ target, level: to });
  const atStatus: EntityAction = {
    id: "follow-status",
    label: m.follow_status(),
    icon: <BellPlus aria-hidden />,
    run: follow("status"),
  };
  const atAll: EntityAction = {
    id: "follow-all",
    label: m.follow_all(),
    icon: <BellRing aria-hidden />,
    run: follow("all"),
  };
  const stop: EntityAction = {
    id: "unfollow",
    label: m.follow_stop(),
    icon: <BellOff aria-hidden />,
    run: follow(null),
  };
  if (!followsChanges(target)) return [level === null ? { ...atStatus, id: "follow", label: m.follow_start() } : stop];
  if (level === null) return [atStatus, atAll];
  return [level === "all" ? atStatus : atAll, stop];
}
